import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Code2, Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { renderTemplate, type EmailTemplate, type EmailTemplateFormat } from '@fenix/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

const LANGUAGE_LABEL: Record<string, string> = {
  'en-US': 'Inglês (en-US)',
  'pt-BR': 'Português (pt-BR)',
};

/** Dados de exemplo da prévia, no idioma do modelo. */
const SAMPLE: Record<string, Record<string, string>> = {
  'en-US': {
    greeting: 'Hi John,',
    first_name: 'John',
    brand: 'HoneyPower',
    company_name: 'Fênix',
    product_name: 'HoneyPower (2 bottles)',
    checkout_url: 'https://checkout.example.com/abc123',
    coupon_code: 'SAVE5',
    offer: '5% off with your coupon',
    coupon_line: 'Use code SAVE5 at checkout — 5% off with your coupon.',
  },
  'pt-BR': {
    greeting: 'Olá, João!',
    first_name: 'João',
    brand: 'HoneyPower',
    company_name: 'Fênix',
    product_name: 'HoneyPower (2 frascos)',
    checkout_url: 'https://checkout.example.com/abc123',
    coupon_code: 'SAVE5',
    offer: '5% de desconto com o cupom',
    coupon_line: 'Use o cupom SAVE5 no checkout — 5% de desconto com o cupom.',
  },
};

type Draft = { subject: string; format: EmailTemplateFormat; body: string };

/** Mesma regra do servidor no modo HTML: valores das variáveis entram escapados. */
const escapeHtml = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function EmailTemplateCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['email-templates'], queryFn: settingsApi.emailTemplates });
  const [language, setLanguage] = useState('en-US');
  const [draft, setDraft] = useState<Draft | null>(null);

  const current: EmailTemplate | undefined = q.data?.templates.find((t) => t.language === language);
  // Troca de idioma ou dado novo do servidor: o rascunho recomeça do que está salvo.
  useEffect(() => {
    if (current) setDraft({ subject: current.subject, format: current.format, body: current.body });
  }, [current]);

  const preview = useMemo(() => {
    if (!draft) return null;
    const vars = SAMPLE[language] ?? SAMPLE['en-US']!;
    const subject = renderTemplate(draft.subject, vars).text;
    if (draft.format === 'html') {
      const safe = Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, escapeHtml(v)]));
      return { subject, html: renderTemplate(draft.body, safe).text, body: null };
    }
    return {
      subject,
      html: null,
      body: renderTemplate(draft.body, vars)
        .text.replace(/\n{3,}/g, '\n\n')
        .trim(),
    };
  }, [draft, language]);

  /** Trocar para HTML começa do HTML padrão (com o botão); voltar para texto, do texto salvo. */
  const switchFormat = (format: EmailTemplateFormat) => {
    if (!draft || draft.format === format) return;
    if (format === 'html') {
      setDraft({ ...draft, format, body: q.data?.htmlStarters[language] ?? '' });
    } else {
      setDraft({ ...draft, format, body: current?.format === 'text' ? current.body : '' });
    }
  };

  const save = useMutation({
    mutationFn: () => settingsApi.saveEmailTemplate(language, draft!),
    onSuccess: () => {
      toast.success('Modelo do e-mail salvo');
      void qc.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar o modelo'),
  });
  const reset = useMutation({
    mutationFn: () => settingsApi.resetEmailTemplate(language),
    onSuccess: () => {
      toast.success('Modelo restaurado para o padrão');
      void qc.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao restaurar'),
  });

  const dirty =
    !!draft &&
    !!current &&
    (draft.subject !== current.subject ||
      draft.body !== current.body ||
      draft.format !== current.format);
  const isHtml = draft?.format === 'html';

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Modelo do e-mail</CardTitle>
            <CardDescription>
              Assunto e corpo por idioma da ligação. Em texto, o link vira botão sozinho; em HTML,
              você controla o visual inteiro.
            </CardDescription>
          </div>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(q.data?.templates ?? []).map((t) => (
                <SelectItem key={t.language} value={t.language}>
                  {LANGUAGE_LABEL[t.language] ?? t.language}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Assunto</Label>
              {current && (
                <Badge variant={current.isDefault ? 'outline' : 'secondary'}>
                  {current.isDefault ? 'padrão do sistema' : 'editado'}
                </Badge>
              )}
            </div>
            <Input
              value={draft?.subject ?? ''}
              onChange={(e) => setDraft((d) => (d ? { ...d, subject: e.target.value } : d))}
              disabled={!isAdmin || !draft}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>{isHtml ? 'Corpo (HTML completo)' : 'Corpo (texto)'}</Label>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={!isHtml ? 'secondary' : 'ghost'}
                  onClick={() => switchFormat('text')}
                  disabled={!isAdmin || !draft}
                >
                  Texto
                </Button>
                <Button
                  size="sm"
                  variant={isHtml ? 'secondary' : 'ghost'}
                  onClick={() => switchFormat('html')}
                  disabled={!isAdmin || !draft}
                >
                  <Code2 /> HTML
                </Button>
              </div>
            </div>
            <Textarea
              rows={isHtml ? 18 : 12}
              className="font-mono text-sm"
              value={draft?.body ?? ''}
              onChange={(e) => setDraft((d) => (d ? { ...d, body: e.target.value } : d))}
              disabled={!isAdmin || !draft}
            />
            <p className="text-xs text-muted-foreground">
              Variáveis:{' '}
              {(q.data?.variables ?? []).map((v) => (
                <span key={v} className="mr-1 inline-block rounded bg-muted px-1.5 font-mono">
                  {`{{${v}}}`}
                </span>
              ))}
              . <span className="font-mono">{'{{checkout_url}}'}</span> é obrigatório;{' '}
              <span className="font-mono">{'{{coupon_line}}'}</span> já vem montada (ou vazia quando
              o produto não tem cupom); <span className="font-mono">{'{{brand}}'}</span> é a marca
              do produto.
              {isHtml &&
                ' No modo HTML, botões, cores e imagens são seus; o HTML de partida já traz o botão do link, e a versão em texto do e-mail é derivada do HTML.'}
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Salvar modelo
              </Button>
              <Button
                variant="outline"
                onClick={() => reset.mutate()}
                disabled={!current || current.isDefault || reset.isPending}
              >
                {reset.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />} Restaurar
                padrão
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Prévia com dados de exemplo</Label>
          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="mb-3 font-medium">{preview?.subject ?? '…'}</p>
            {preview?.html != null ? (
              <iframe
                title="Prévia do e-mail"
                sandbox=""
                srcDoc={preview.html}
                className="h-[480px] w-full rounded border bg-white"
              />
            ) : (
              <pre className="font-sans whitespace-pre-wrap [overflow-wrap:anywhere]">
                {preview?.body ?? ''}
              </pre>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
